import React, { useState, useEffect, useCallback } from 'react';
import { 
  User, 
  Search, 
  Filter,
  Trash2,
  Users,
  XCircle,
  UserPlus,
  GraduationCap,
  Clock,
  Target,
  BookOpen,
  DollarSign,
  Receipt,
  Calendar
} from 'lucide-react';
import { 
  collection, 
  doc,
  updateDoc,
  addDoc,
  query, 
  where, 
  getDocs,
  getDoc,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import teacherBatchSync from '../../services/teacherBatchSync';
import { db } from '../../firebase/config';
import { useRealtime } from '../../contexts/RealtimeContext';
import TeacherSalaryManagement from './TeacherSalaryManagement';
import TeacherAttendanceReport from './TeacherAttendanceReport';
import TeacherAttendanceMarking from './TeacherAttendanceMarkingOptimized';
import MasterTimetableManagement from '../../pages/admin/MasterTimetableManagement';
import toast from 'react-hot-toast';

const TeacherManagement = ({ coachingId }) => {
  const { currentUser, userProfile } = useRealtime();
  const effectiveCoachingId = coachingId || userProfile?.coachingId || null;
  
  // All hooks must be at the top level - fix React hooks rules violation
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBatchAssignModal, setShowBatchAssignModal] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [newTeacherEmail, setNewTeacherEmail] = useState('');
  const [newTeacherData, setNewTeacherData] = useState({
    displayName: '',
    subject: '',
    qualification: '',
    experience: '',
    phone: ''
  });
  const [batches, setBatches] = useState([]);
  const [selectedBatches, setSelectedBatches] = useState([]);
  const [addingTeacher, setAddingTeacher] = useState(false);
  const [assigningBatch, setAssigningBatch] = useState(false);
  const [showSalaryModal, setShowSalaryModal] = useState(false);
  const [selectedTeacherForSalary, setSelectedTeacherForSalary] = useState(null);
  const [showAttendanceReport, setShowAttendanceReport] = useState(false);
  const [showAttendanceMarking, setShowAttendanceMarking] = useState(false);
  const [showTimetable, setShowTimetable] = useState(false);



  // Helper function to remove undefined values from objects
  const cleanUndefinedValues = (obj) => {
    const cleaned = {};
    Object.keys(obj).forEach(key => {
      if (obj[key] !== undefined && obj[key] !== null) {
        if (typeof obj[key] === 'object' && !Array.isArray(obj[key]) && obj[key].constructor === Object) {
          cleaned[key] = cleanUndefinedValues(obj[key]);
        } else {
          cleaned[key] = obj[key];
        }
      }
    });
    return cleaned;
  };

  const loadTeachers = useCallback(async () => {
    try {
      setLoading(true);
      
      // Check cache first (5 minute TTL)
      const cacheKey = `teachers_${coachingId}`;
      const cached = localStorage.getItem(cacheKey);
      const cacheTs = localStorage.getItem(`${cacheKey}_ts`);
      
      if (cached && cacheTs && (Date.now() - parseInt(cacheTs)) < 300000) {
        setTeachers(JSON.parse(cached));
        setLoading(false);
        return;
      }

      const teachersQuery = query(
        collection(db, 'users'),
        where('role', '==', 'teacher'),
        where('coachingId', '==', coachingId)
      );
      
      const snapshot = await getDocs(teachersQuery);
      const teachersList = [];
      
      snapshot.forEach((doc) => {
        teachersList.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      setTeachers(teachersList);
      
      // Cache the results
      localStorage.setItem(cacheKey, JSON.stringify(teachersList));
      localStorage.setItem(`${cacheKey}_ts`, Date.now().toString());
    } catch (error) {
      toast.error('Failed to load teachers');
    } finally {
      setLoading(false);
    }
  }, [coachingId]);

  const loadBatches = useCallback(async () => {
    try {
      // Check cache first (5 minute TTL)
      const cacheKey = `batches_${coachingId}`;
      const cached = localStorage.getItem(cacheKey);
      const cacheTs = localStorage.getItem(`${cacheKey}_ts`);
      
      if (cached && cacheTs && (Date.now() - parseInt(cacheTs)) < 300000) {
        setBatches(JSON.parse(cached));
        return;
      }

      const batchesQuery = query(
        collection(db, 'batches'),
        where('coachingId', '==', coachingId)
      );
      
      const snapshot = await getDocs(batchesQuery);
      const batchesList = [];
      
      snapshot.forEach((doc) => {
        batchesList.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      setBatches(batchesList);
      
      // Cache the results
      localStorage.setItem(cacheKey, JSON.stringify(batchesList));
      localStorage.setItem(`${cacheKey}_ts`, Date.now().toString());
    } catch (error) {
      console.error('Error loading batches:', error);
    }
  }, [coachingId]);

  // Load teachers and batches with caching and polling
  useEffect(() => {
    if (coachingId) {
      loadTeachers();
      loadBatches();
      
      // Refresh every 5 minutes
      const refreshInterval = setInterval(() => {
        loadTeachers();
        loadBatches();
      }, 300000);
      
      return () => clearInterval(refreshInterval);
    }
  }, [coachingId, loadTeachers, loadBatches]);

  const handleAddTeacher = async () => {
    if (!newTeacherEmail.trim()) {
      toast.error('Please enter teacher email');
      return;
    }

    if (!effectiveCoachingId) {
      toast.error('Coaching ID is missing. Please contact support.');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newTeacherEmail.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Check user permissions
    const canManageTeachers = userProfile && (
      userProfile.role === 'admin' ||
      userProfile.role === 'superadmin' ||
      userProfile.role === 'coaching_admin' ||
      (userProfile.role === 'coaching_staff' && userProfile.accessibleSections?.includes('teachers'))
    );

    if (!canManageTeachers) {
      toast.error('You do not have permission to add teachers. Please ensure you are logged in as a coaching admin or coaching staff with Teacher Management access.');
      return;
    }

    setAddingTeacher(true);
    
    try {
      // Check if user exists
      const usersQuery = query(
        collection(db, 'users'),
        where('email', '==', newTeacherEmail.toLowerCase())
      );
      
      const userSnapshot = await getDocs(usersQuery);
      
      if (userSnapshot.empty) {
        // Create new teacher account
        const teacherData = {
          email: newTeacherEmail.toLowerCase(),
          displayName: newTeacherData.displayName || newTeacherEmail.split('@')[0],
          role: 'teacher',
          coachingId: effectiveCoachingId,
          teacherProfile: {
            subject: newTeacherData.subject || '',
            qualification: newTeacherData.qualification || '',
            experience: newTeacherData.experience || '',
            phone: newTeacherData.phone || '',
            batches: [],
            isActive: true,
            joinedAt: serverTimestamp(),
            permissions: {
              canManageBatches: true,
              canMarkAttendance: true,
              canUploadMaterials: true,
              canCreateAssignments: true,
              canViewReports: true
            }
          },
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          createdBy: currentUser?.uid
        };
        
        const docRef = await addDoc(collection(db, 'users'), teacherData);
        
        toast.success('Teacher account created successfully!');
      } else {
        // Update existing user to teacher role
        const existingUser = userSnapshot.docs[0];
        const userData = existingUser.data();
        
        if (userData.role === 'teacher' && userData.coachingId === coachingId) {
          toast.error('Teacher is already part of this coaching');
          return;
        }
        
        if (userData.role === 'teacher' && userData.coachingId && userData.coachingId !== coachingId) {
          toast.error('Teacher is already associated with another coaching center');
          return;
        }
        
        const updateData = {
          role: 'teacher',
          coachingId: effectiveCoachingId,
          teacherProfile: {
            ...userData.teacherProfile,
            subject: newTeacherData.subject || userData.teacherProfile?.subject || '',
            qualification: newTeacherData.qualification || userData.teacherProfile?.qualification || '',
            experience: newTeacherData.experience || userData.teacherProfile?.experience || '',
            phone: newTeacherData.phone || userData.teacherProfile?.phone || '',
            batches: userData.teacherProfile?.batches || [],
            isActive: true,
            joinedAt: userData.teacherProfile?.joinedAt || serverTimestamp(),
            permissions: {
              canManageBatches: true,
              canMarkAttendance: true,
              canUploadMaterials: true,
              canCreateAssignments: true,
              canViewReports: true
            }
          },
          updatedAt: serverTimestamp(),
          updatedBy: currentUser?.uid
        };
        
        await updateDoc(doc(db, 'users', existingUser.id), updateData);
        
        toast.success('Teacher added to coaching successfully!');
      }
      
      // Invalidate cache
      localStorage.removeItem(`teachers_${coachingId}`);
      localStorage.removeItem(`teachers_${coachingId}_ts`);
      
      // Reset form and reload
      setNewTeacherEmail('');
      setNewTeacherData({
        displayName: '',
        subject: '',
        qualification: '',
        experience: '',
        phone: ''
      });
      setShowAddModal(false);
      loadTeachers();
      
    } catch (error) {
      
      // More specific error messages
      if (error.code === 'permission-denied') {
        toast.error('Permission denied. You may not have the required permissions to add teachers.');
      } else if (error.code === 'invalid-argument') {
        toast.error('Invalid data provided. Please check all fields.');
      } else if (error.code === 'failed-precondition') {
        toast.error('Database operation failed. Please try again.');
      } else if (error.message?.includes('index')) {
        toast.error('Database index error. Please contact support.');
      } else if (error.message?.includes('network')) {
        toast.error('Network error. Please check your connection and try again.');
      } else {
        toast.error(`Failed to add teacher: ${error.message}`);
      }
    } finally {
      setAddingTeacher(false);
    }
  };

  const handleAssignBatches = async () => {
    if (!selectedTeacher || selectedBatches.length === 0) {
      toast.error('Please select batches to assign');
      return;
    }

    // Validate teacher data
    if (!selectedTeacher.id) {
      toast.error('Invalid teacher selected. Please refresh and try again.');
      return;
    }

    setAssigningBatch(true);
    try {
      const batch = writeBatch(db);
      
      // Update teacher's batches
      const teacherRef = doc(db, 'users', selectedTeacher.id);
      const currentBatches = selectedTeacher.teacherProfile?.batches || [];
      const newBatches = [...currentBatches];
      
      // Add new batches to teacher
      selectedBatches.forEach(batchId => {
        if (!newBatches.find(b => b.id === batchId)) {
          const batchData = batches.find(b => b.id === batchId);
          newBatches.push({
            id: batchId,
            name: batchData?.name || 'Unknown Batch',
            subject: batchData?.subject || 'Unknown Subject',
            assignedAt: new Date()
          });
        }
      });
      
      // Ensure teacher profile exists and has proper structure
      let teacherUpdateData = {
        'teacherProfile.batches': newBatches,
        updatedAt: serverTimestamp()
      };
      
      // If teacher profile doesn't exist, create it
      if (!selectedTeacher.teacherProfile) {
        teacherUpdateData = {
          teacherProfile: {
            batches: newBatches,
            isActive: true,
            permissions: {
              canManageBatches: true,
              canMarkAttendance: true,
              canUploadMaterials: true,
              canCreateAssignments: true,
              canViewReports: true
            }
          },
          updatedAt: serverTimestamp()
        };
      }
      
      // Clean undefined values
      teacherUpdateData = cleanUndefinedValues(teacherUpdateData);
      
      batch.update(teacherRef, teacherUpdateData);
      
      // Update batches with teacher assignment (support multiple teachers)
      for (const batchId of selectedBatches) {
        const batchRef = doc(db, 'batches', batchId);
        const batchDoc = await getDoc(batchRef);
        
        if (batchDoc.exists()) {
          const batchData = batchDoc.data();
          
          // Get existing teacherIds array or create from legacy teacherId
          let teacherIds = batchData.teacherIds || [];
          if (batchData.teacherId && !teacherIds.includes(batchData.teacherId)) {
            teacherIds.push(batchData.teacherId);
          }
          
          // Add new teacher if not already in array
          if (!teacherIds.includes(selectedTeacher.id)) {
            teacherIds.push(selectedTeacher.id);
          }
          
          // Get existing teachers array or create new
          let teachers = batchData.teachers || [];
          if (!teachers.find(t => t.id === selectedTeacher.id)) {
            teachers.push({
              id: selectedTeacher.id,
              name: selectedTeacher.displayName || selectedTeacher.email || 'Unknown Teacher',
              email: selectedTeacher.email || '',
              assignedAt: new Date()
            });
          }
          
          const updateData = cleanUndefinedValues({
            teacherIds: teacherIds,
            teachers: teachers,
            // Keep legacy fields for backward compatibility
            teacherId: teacherIds[0] || selectedTeacher.id,
            teacherName: selectedTeacher.displayName || selectedTeacher.email || 'Unknown Teacher',
            teacherEmail: selectedTeacher.email || '',
            updatedAt: serverTimestamp()
          });
          
          batch.update(batchRef, updateData);
        }
      }
      
      await batch.commit();
      
      // Update teacher dashboard with new batch assignments
      try {
        const updatedBatches = selectedTeacher.teacherProfile?.batches || [];
        await teacherBatchSync.createTeacherDashboardData(selectedTeacher.id, updatedBatches);
      } catch (dashboardError) {
        // Don't fail the whole operation if dashboard update fails
      }
      
      // Invalidate cache
      localStorage.removeItem(`teachers_${coachingId}`);
      localStorage.removeItem(`teachers_${coachingId}_ts`);
      localStorage.removeItem(`batches_${coachingId}`);
      localStorage.removeItem(`batches_${coachingId}_ts`);
      
      toast.success('Batches assigned successfully! Teacher dashboard updated.');
      setShowBatchAssignModal(false);
      setSelectedTeacher(null);
      setSelectedBatches([]);
      loadTeachers();
      
    } catch (error) {
      
      // More specific error messages
      if (error.code === 'permission-denied') {
        toast.error('Permission denied. You may not have the required permissions to assign batches.');
      } else if (error.code === 'not-found') {
        toast.error('Teacher or batch not found. Please refresh and try again.');
      } else if (error.code === 'failed-precondition') {
        toast.error('Database operation failed. Please try again.');
      } else {
        toast.error(`Failed to assign batches: ${error.message}`);
      }
    } finally {
      setAssigningBatch(false);
    }
  };

  const handleRemoveTeacher = async (teacherId) => {
    if (!window.confirm('Are you sure you want to remove this teacher?')) {
      return;
    }

    try {
      // Update teacher role to student or remove coaching association
      await updateDoc(doc(db, 'users', teacherId), {
        role: 'student',
        coachingId: null,
        teacherProfile: null,
        updatedAt: serverTimestamp()
      });
      
      // Remove teacher from all batches
      const teacherBatches = teachers.find(t => t.id === teacherId)?.teacherProfile?.batches || [];
      const batch = writeBatch(db);
      
      teacherBatches.forEach(batchData => {
        const batchRef = doc(db, 'batches', batchData.id);
        batch.update(batchRef, {
          teacherId: null,
          teacherName: null,
          teacherEmail: null,
          updatedAt: serverTimestamp()
        });
      });
      
      await batch.commit();
      
      // Invalidate cache
      localStorage.removeItem(`teachers_${coachingId}`);
      localStorage.removeItem(`teachers_${coachingId}_ts`);
      localStorage.removeItem(`batches_${coachingId}`);
      localStorage.removeItem(`batches_${coachingId}_ts`);
      
      toast.success('Teacher removed successfully');
      loadTeachers();
      
    } catch (error) {
      toast.error('Failed to remove teacher');
    }
  };

  const filteredTeachers = teachers.filter(teacher => {
    const matchesSearch = teacher.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         teacher.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         teacher.teacherProfile?.subject?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' || 
                         (filterStatus === 'active' && teacher.teacherProfile?.isActive) ||
                         (filterStatus === 'inactive' && !teacher.teacherProfile?.isActive);
    
    return matchesSearch && matchesFilter;
  });

  // Show error if no coachingId
  if (!coachingId) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Configuration Error
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Coaching ID is missing. Please contact support.
          </p>
          <div className="text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 p-3 rounded">
            <p><strong>Debug Info:</strong></p>
            <p>User Profile: {userProfile ? JSON.stringify({
              id: userProfile.id,
              role: userProfile.role,
              coachingId: userProfile.coachingId,
              email: userProfile.email
            }) : 'Not loaded'}</p>
            <p>Current User: {currentUser?.uid || 'Not authenticated'}</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
              <GraduationCap className="w-6 h-6 mr-2 text-blue-600" />
              Teacher Management
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Manage teachers, assign batches, manage salaries, and view attendance reports
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => {
                console.log('Manage Timetable button clicked');
                setShowTimetable(true);
              }}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
            >
              <Calendar className="w-5 h-5" />
              <span>Manage Timetable</span>
            </button>
            <button
              onClick={() => setShowAttendanceMarking(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
            >
              <Calendar className="w-5 h-5" />
              <span>Mark Attendance</span>
            </button>
            <button
              onClick={() => setShowAttendanceReport(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
            >
              <Receipt className="w-5 h-5" />
              <span>Attendance Report</span>
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              <UserPlus className="w-5 h-5" />
              <span>Add Teacher</span>
            </button>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search teachers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="all">All Teachers</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Teachers List */}
      <div className="p-6">
        {filteredTeachers.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No Teachers Found
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {teachers.length === 0 
                ? "Start by adding teachers to your coaching center"
                : "No teachers match your search criteria"
              }
            </p>
            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Add First Teacher
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTeachers.map((teacher) => (
              <div key={teacher.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {teacher.displayName || teacher.email}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {teacher.email}
                      </p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    teacher.teacherProfile?.isActive 
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                  }`}>
                    {teacher.teacherProfile?.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <BookOpen className="w-4 h-4 mr-2" />
                    <span>{teacher.teacherProfile?.subject || 'No subject assigned'}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <Users className="w-4 h-4 mr-2" />
                    <span>{teacher.teacherProfile?.batches?.length || 0} batches</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <Clock className="w-4 h-4 mr-2" />
                    <span>{teacher.teacherProfile?.experience || 'Experience not specified'}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <DollarSign className="w-4 h-4 mr-2" />
                    <span>
                      {teacher.teacherProfile?.salary?.baseSalary 
                        ? `₹${teacher.teacherProfile.salary.baseSalary.toLocaleString()}/month`
                        : 'Salary not set'
                      }
                    </span>
                  </div>
                </div>

                <div className="flex flex-col space-y-2">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        setSelectedTeacher(teacher);
                        setShowBatchAssignModal(true);
                      }}
                      className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                    >
                      <Target className="w-4 h-4 inline mr-1" />
                      Assign Batches
                    </button>
                    <button
                      onClick={() => {
                        setSelectedTeacherForSalary(teacher);
                        setShowSalaryModal(true);
                      }}
                      className="flex-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                    >
                      <DollarSign className="w-4 h-4 inline mr-1" />
                      Manage Salary
                    </button>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleRemoveTeacher(teacher.id)}
                      className="flex-1 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                    >
                      <Trash2 className="w-4 h-4 inline mr-1" />
                      Remove Teacher
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Teacher Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">Add Teacher</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={newTeacherEmail}
                  onChange={(e) => setNewTeacherEmail(e.target.value)}
                  placeholder="teacher@example.com"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Display Name
                </label>
                <input
                  type="text"
                  value={newTeacherData.displayName}
                  onChange={(e) => setNewTeacherData(prev => ({ ...prev, displayName: e.target.value }))}
                  placeholder="Teacher's full name"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Subject
                </label>
                <input
                  type="text"
                  value={newTeacherData.subject}
                  onChange={(e) => setNewTeacherData(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="Mathematics, Physics, etc."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Qualification
                </label>
                <input
                  type="text"
                  value={newTeacherData.qualification}
                  onChange={(e) => setNewTeacherData(prev => ({ ...prev, qualification: e.target.value }))}
                  placeholder="M.Sc, B.Tech, etc."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Experience
                </label>
                <input
                  type="text"
                  value={newTeacherData.experience}
                  onChange={(e) => setNewTeacherData(prev => ({ ...prev, experience: e.target.value }))}
                  placeholder="5 years, 2+ years, etc."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={newTeacherData.phone}
                  onChange={(e) => setNewTeacherData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="+91 9876543210"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleAddTeacher}
                disabled={addingTeacher || !newTeacherEmail.trim()}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50"
              >
                {addingTeacher ? 'Adding...' : 'Add Teacher'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Batch Assignment Modal */}
      {showBatchAssignModal && selectedTeacher && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                Assign Batches to {selectedTeacher.displayName}
              </h3>
              <button
                onClick={() => setShowBatchAssignModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Select batches to assign to this teacher:
              </p>

              <div className="max-h-60 overflow-y-auto space-y-2">
                {batches.map((batch) => {
                  const isAssigned = selectedTeacher.teacherProfile?.batches?.some(b => b.id === batch.id);
                  const isSelected = selectedBatches.includes(batch.id);
                  
                  return (
                    <div key={batch.id} className="flex items-center space-x-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedBatches(prev => [...prev, batch.id]);
                          } else {
                            setSelectedBatches(prev => prev.filter(id => id !== batch.id));
                          }
                        }}
                        disabled={isAssigned}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 dark:text-white">
                          {batch.name}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {batch.subject} • {batch.students?.length || 0} students
                        </p>
                      </div>
                      {isAssigned && (
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                          Already Assigned
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowBatchAssignModal(false)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignBatches}
                disabled={assigningBatch || selectedBatches.length === 0}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50"
              >
                {assigningBatch ? 'Assigning...' : `Assign ${selectedBatches.length} Batches`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Salary Management Modal */}
      {showSalaryModal && selectedTeacherForSalary && (
        <TeacherSalaryManagement
          teacher={selectedTeacherForSalary}
          onClose={() => {
            setShowSalaryModal(false);
            setSelectedTeacherForSalary(null);
          }}
          onUpdate={() => {
            // Invalidate cache when salary is updated
            localStorage.removeItem(`teachers_${coachingId}`);
            localStorage.removeItem(`teachers_${coachingId}_ts`);
            loadTeachers(); // Reload teachers to show updated salary info
          }}
        />
      )}

      {/* Teacher Attendance Marking Modal */}
      {showAttendanceMarking && (
        <TeacherAttendanceMarking
          coachingId={coachingId}
          onClose={() => setShowAttendanceMarking(false)}
        />
      )}

      {/* Teacher Attendance Report Modal */}
      {showAttendanceReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-7xl w-full max-h-[95vh] overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                Teacher Attendance & Salary Report
              </h3>
              <button
                onClick={() => setShowAttendanceReport(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            <div className="overflow-y-auto max-h-[calc(95vh-80px)]">
              <TeacherAttendanceReport coachingId={coachingId} />
            </div>
          </div>
        </div>
      )}

      {/* Timetable Management */}
      {console.log('showTimetable:', showTimetable)}
      {showTimetable && (
        <MasterTimetableManagement coachingId={coachingId} onClose={() => setShowTimetable(false)} />
      )}
    </div>
  );
};

export default TeacherManagement;